import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout, isUserNotFoundError } from '../utils/auth';
import { useLanguage } from '../context/LanguageContext';
import AppHeader from '../components/common/AppHeader';

// ═══════════════════════════════════════════════════════════════
// 3 palettes — synced with Login page
// ═══════════════════════════════════════════════════════════════
const PALETTES = {
  B: {
    key: 'B', name: 'Sunset', emoji: '🌅',
    primary: '#FF5C5C', primaryShadow: '#B83838',
    accent: '#FFD23F', accent2: '#3DC9B5',
    sunset: '#FF8A3D', coral: '#FF5C5C', plum: '#5B2E5E', aqua: '#3DC9B5',
    ink: '#2A1F3D', cream: '#FFF4E6', sand: '#FFE4C4',
    mutedText: '#7A6B85',
    heroStart: '#FFB86B', heroMid: '#FF8A3D', heroEnd: '#FF5C5C',
    blob1: '#FFD23F', blob2: '#FF5C5C',
    stat1Bg: '#FFD23F', stat3Bg: '#3DC9B5', stat4Bg: '#FFE4E4',
  },
  A: {
    key: 'A', name: 'Forest', emoji: '🌲',
    primary: '#2A6B30', primaryShadow: '#1E5C2E',
    accent: '#E8A020', accent2: '#67A844',
    sunset: '#E8A020', coral: '#2A6B30', plum: '#1E5C2E', aqua: '#67A844',
    ink: '#3E2B1E', cream: '#F8F6F0', sand: '#F0EAD6',
    mutedText: '#A67C52',
    heroStart: '#EAF2EA', heroMid: '#67A844', heroEnd: '#1E5C2E',
    blob1: '#E8A020', blob2: '#67A844',
    stat1Bg: '#E8A020', stat3Bg: '#67A844', stat4Bg: '#F8ECDC',
  },
  C: {
    key: 'C', name: 'Ocean', emoji: '🌊',
    primary: '#5B4EC7', primaryShadow: '#3B2F9E',
    accent: '#FFD23F', accent2: '#16A085',
    sunset: '#8B7CF6', coral: '#5B4EC7', plum: '#3B2F9E', aqua: '#16A085',
    ink: '#1A1A2E', cream: '#F5F3FF', sand: '#EEE9FF',
    mutedText: '#6A6A80',
    heroStart: '#B8A9FF', heroMid: '#8B7CF6', heroEnd: '#5B4EC7',
    blob1: '#FFD23F', blob2: '#16A085',
    stat1Bg: '#FFD23F', stat3Bg: '#16A085', stat4Bg: '#EEE9FF',
  },
};

const FONT = '"Nunito", "Baloo 2", sans-serif';

// ═══════════════════════════════════════════════════════════════
// Icons
// ═══════════════════════════════════════════════════════════════
const Icon = ({ name, size = 20, color = 'currentColor', stroke = 2.2 }) => {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: stroke, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'home': return (<svg {...p}><path d="M3 10.5L12 3l9 7.5V20a2 2 0 01-2 2h-4v-7h-6v7H5a2 2 0 01-2-2v-9.5z"/></svg>);
    case 'map': return (<svg {...p}><polygon points="1 6 8 3 16 6 23 3 23 18 16 21 8 18 1 21 1 6"/><line x1="8" y1="3" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="21"/></svg>);
    case 'game': return (<svg {...p}><rect x="2" y="7" width="20" height="12" rx="3"/><line x1="8" y1="13" x2="10" y2="13"/><line x1="9" y1="12" x2="9" y2="14"/><circle cx="15" cy="12" r="0.7" fill={color}/><circle cx="17" cy="14" r="0.7" fill={color}/></svg>);
    case 'book': return (<svg {...p}><path d="M4 4h12a3 3 0 013 3v14H7a3 3 0 01-3-3V4z"/><path d="M4 4v13a3 3 0 003 3"/></svg>);
    case 'test': return (<svg {...p}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></svg>);
    case 'target': return (<svg {...p}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2" fill={color}/></svg>);
    case 'bell': return (<svg {...p}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 01-3.4 0"/></svg>);
    case 'star': return (<svg viewBox="0 0 24 24" width={size} height={size} fill={color}><polygon points="12 2 15 9 22 10 17 15 18 22 12 18 6 22 7 15 2 10 9 9"/></svg>);
    case 'play': return (<svg viewBox="0 0 24 24" width={size} height={size} fill={color}><polygon points="6,4 20,12 6,20"/></svg>);
    case 'check': return (<svg {...p}><polyline points="4 12 10 18 20 6"/></svg>);
    case 'sparkle': return (<svg viewBox="0 0 24 24" width={size} height={size} fill={color}><path d="M12 2L14 10L22 12L14 14L12 22L10 14L2 12L10 10z"/></svg>);
    case 'palette': return (<svg {...p}><circle cx="12" cy="12" r="10"/><circle cx="7.5" cy="10.5" r=".5" fill={color}/><circle cx="16.5" cy="10.5" r=".5" fill={color}/><circle cx="12" cy="7.5" r=".5" fill={color}/><circle cx="12" cy="16.5" r=".5" fill={color}/></svg>);
    case 'search': return (<svg {...p}><circle cx="11" cy="11" r="7"/><path d="M21 21l-5-5"/></svg>);
    case 'menu': return (<svg {...p}><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>);
    case 'chevron-left': return (<svg {...p}><polyline points="15 18 9 12 15 6"/></svg>);
    case 'chevron-right': return (<svg {...p}><polyline points="9 18 15 12 9 6"/></svg>);
    default: return null;
  }
};

const Owl = ({ size = 30 }) => (
  <div style={{ fontSize: size * 0.85, lineHeight: 1, display: 'inline-block', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}>🦉</div>
);

// ═══════════════════════════════════════════════════════════════
// Palette switcher (top-right, same as Login)
// ═══════════════════════════════════════════════════════════════
const PaletteSwitcher = ({ current, onChange }) => (
  <div style={{
    position: 'fixed', top: 16, right: 16, zIndex: 1000,
    display: 'flex', gap: 8, alignItems: 'center',
    background: 'rgba(255,255,255,0.95)', padding: '8px 12px', borderRadius: 999,
    border: '2px solid rgba(0,0,0,0.15)', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    backdropFilter: 'blur(8px)', fontFamily: 'system-ui, sans-serif',
  }}>
    <Icon name="palette" size={16} color="#1a1a2e" />
    <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>Màu:</span>
    {Object.values(PALETTES).map(p => {
      const active = current === p.key;
      return (
        <button key={p.key} onClick={() => onChange(p.key)} title={`${p.emoji} ${p.name}`} style={{
          width: 28, height: 28, borderRadius: '50%',
          background: `linear-gradient(135deg, ${p.heroStart}, ${p.heroEnd})`,
          border: active ? `3px solid ${p.ink}` : '2px solid rgba(0,0,0,0.15)',
          boxShadow: active ? `0 0 0 2px #fff, 0 2px 6px rgba(0,0,0,0.2)` : 'none',
          cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s', transform: active ? 'scale(1.1)' : 'scale(1)',
        }}>
          {active && <Icon name="check" size={14} color="#fff" stroke={3} />}
        </button>
      );
    })}
  </div>
);

// ═══════════════════════════════════════════════════════════════
// Dashboard main component
// ═══════════════════════════════════════════════════════════════
const API = process.env.REACT_APP_API_URL;
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  accept: 'application/json',
});

const Dashboard = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const userName = localStorage.getItem('userFullName') || 'Minh';

  const P = PALETTES.B;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [showMobileUserMenu, setShowMobileUserMenu] = useState(false);
  const mobileUserMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileUserMenuRef.current && !mobileUserMenuRef.current.contains(e.target)) {
        setShowMobileUserMenu(false);
      }
    };
    if (showMobileUserMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMobileUserMenu]);
  useEffect(() => { localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed)); }, [sidebarCollapsed]);

  const [streakInfo, setStreakInfo] = useState(null);
  const [starsInfo, setStarsInfo] = useState(null);
  const [questsData, setQuestsData] = useState(null);
  const [hub, setHub] = useState(null);
  const [journey, setJourney] = useState(null);
  const [levelProgress, setLevelProgress] = useState(null);

  const today = new Date().toLocaleDateString(lang === 'vi' ? 'vi-VN' : 'en-US', {
    weekday: 'long', day: 'numeric', month: 'numeric', year: 'numeric',
  });
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Chào buổi sáng' : hour < 18 ? 'Chào buổi chiều' : 'Chào buổi tối';
  const greetingEmoji = hour < 12 ? '🌞' : hour < 18 ? '☀️' : '🌙';

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    // allSettled: one failing endpoint must not blank the whole dashboard.
    Promise.allSettled([
      fetch(`${API}/client/streak/`, { headers: authHeader() }),
      fetch(`${API}/client/stars/`, { headers: authHeader() }),
      fetch(`${API}/client/daily-quests/`, { headers: authHeader() }),
      fetch(`${API}/client/dashboard/`, { headers: authHeader() }),
      fetch(`${API}/client/topics/recommendations/journey`, { headers: authHeader() }),
      fetch(`${API}/client/vocab/level-progress`, { headers: authHeader() }),
    ]).then(async (results) => {
      const [sr, st, qr, dr, jr, lp] = results.map(r => r.status === 'fulfilled' ? r.value : null);
      for (const res of [sr, st, qr]) {
        if (res && res.status === 404) {
          const e = await res.json().catch(() => ({}));
          if (isUserNotFoundError(res, e)) { logout(navigate); return; }
        }
      }
      if (sr && sr.ok) setStreakInfo(await sr.json());
      if (st && st.ok) setStarsInfo(await st.json());
      if (qr && qr.ok) setQuestsData(await qr.json());
      if (dr && dr.ok) setHub(await dr.json());
      if (jr && jr.ok) setJourney(await jr.json());
      if (lp && lp.ok) setLevelProgress(await lp.json());
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived values for UI ──
  const streak = streakInfo?.current || 0;
  const shields = streakInfo?.shields || 0;
  const stars = starsInfo?.total_stars || 0;
  const readiness = hub?.overall_score ? Math.round(hub.overall_score) : 0;
  const weakItems = (hub?.needs_improvement || []).slice(0, 4).map(i => ({
    name: i.name, score: Math.round(i.score),
    tag: i.type === 'part' ? 'SKILL' : i.type === 'grammar' ? 'GRAMMAR' : i.type === 'topic' ? 'TOPIC' : 'VOCAB',
    url: i.cta?.url || '/topics',
  }));
  const recommendedTopics = (hub?.recommended_topics || []).slice(0, 5);
  const recentlyMastered = (hub?.recently_mastered || []).slice(0, 5);
  const questsArr = questsData?.quests || [];
  const questDone = questsArr.filter(q => q.is_completed).length;
  const questTotal = questsArr.length || 3;

  // ─── Sidebar nav items (desktop only) ───
  const navItems = [
    { i: 'home', l: 'Trang chủ', url: '/dashboard', active: true, badge: null },
    { i: 'map', l: 'Chủ điểm', url: '/topics', badge: 'NEW' },
    { i: 'book', l: 'Thư viện', url: '/vocab/words', badge: null },
    { i: 'test', l: 'Đề Thi', url: '/full-mock-test', badge: null },
    { i: 'target', l: 'Thành tích', url: '/report', badge: null },
  ];

  // ═══════════════════════════════════════════════════════════════
  // DESKTOP LAYOUT (≥lg = 1024px) — HuPath v2 (Fredoka + yellow/green)
  // ═══════════════════════════════════════════════════════════════
  const V2 = '/images/dashboard-v2';
  const HT = {
    yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
    green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9', greenShield: '#C9E4A5',
    cream: '#FFF8E7',
    coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
    pinkBg: '#FCE4E8', pink: '#F4A6B8',
    brown: '#8B6F47', brownDark: '#6B4F2B',
    text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
    border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
  };
  const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
  const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';
  const SHADOW_SM = '0 2px 6px rgba(139,111,71,0.08)';
  const SHADOW_MD = '0 4px 16px rgba(139,111,71,0.10)';

  // ─── Level vocabulary learning (continuous flow) ───
  const LEVEL_META = {
    Starters: { icon: '🐣', accent: '#5E9E3E', bg: '#E8F3D9' },
    Movers: { icon: '🚀', accent: '#1e40af', bg: '#EFF6FF' },
    Flyers: { icon: '✈️', accent: '#E76F51', bg: '#FCE4E8' },
    KET: { icon: '🎓', accent: '#8B6F47', bg: '#FFF8E7' },
  };
  const vocabLevels = levelProgress?.levels || [];
  const currentLevelId = levelProgress?.current_level_id;
  const renderLevelVocab = () => {
    if (!vocabLevels.length) return null;
    return (
      <section style={{
        background: HT.white, borderRadius: 16, padding: 20,
        boxShadow: SHADOW_SM, border: `1px solid ${HT.border}`,
        marginBottom: 24,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div style={{
              fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 600,
              color: HT.text, display: 'flex', alignItems: 'center', gap: 8,
            }}>📖 Học từ vựng theo cấp độ</div>
            <div style={{ fontSize: 13, color: HT.textMuted, marginTop: 2 }}>
              Chọn cấp độ để học liên tục — ôn từ đã thuộc + học từ mới
            </div>
          </div>
          {levelProgress?.daily_goal != null && (
            <span style={{
              padding: '6px 14px', borderRadius: 999,
              background: (levelProgress.today_words || 0) >= levelProgress.daily_goal ? HT.greenBg : HT.yellowBg,
              border: `1px solid ${(levelProgress.today_words || 0) >= levelProgress.daily_goal ? HT.green : HT.yellow}`,
              fontSize: 13, fontWeight: 800, color: HT.brownDark, fontFamily: FONT_DISPLAY,
              whiteSpace: 'nowrap',
            }}>
              🎯 Hôm nay: {levelProgress.today_words || 0}/{levelProgress.daily_goal} từ
              {(levelProgress.today_words || 0) >= levelProgress.daily_goal ? ' ✓' : ''}
            </span>
          )}
        </div>
        <div style={{
          display: 'grid', gap: 12,
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        }}>
          {vocabLevels.map((lv) => {
            const meta = LEVEL_META[lv.level] || { icon: '📘', accent: HT.greenDark, bg: HT.greenBg };
            const isCurrent = lv.level_id === currentLevelId;
            return (
              <div
                key={lv.level_id}
                onClick={() => navigate(`/vocab/${encodeURIComponent(lv.level.toLowerCase())}/play`)}
                style={{
                  background: meta.bg,
                  border: isCurrent ? `2px solid ${meta.accent}` : `2px solid ${meta.accent}22`,
                  borderRadius: 14, padding: 14, cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = SHADOW_MD; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ fontSize: 28 }}>{meta.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, color: HT.text }}>
                      {lv.level}
                      {isCurrent && <span style={{ fontSize: 10, color: meta.accent, marginLeft: 6, fontWeight: 700 }}>• của bạn</span>}
                    </div>
                    <div style={{ fontSize: 12, color: HT.text, marginTop: 2, fontWeight: 700 }}>
                      {lv.in_progress > 0
                        ? <>📖 {lv.in_progress} đang học · ✅ {lv.learned} thuộc</>
                        : <>✅ {lv.learned} thuộc / {lv.total} từ</>}
                    </div>
                  </div>
                </div>
                {/* learned (solid) + in-progress (soft yellow) stacked on one bar */}
                <div style={{ marginTop: 10, height: 5, background: '#fff', borderRadius: 3, overflow: 'hidden', display: 'flex' }}>
                  <div style={{ width: `${lv.mastery_pct}%`, height: '100%', background: meta.accent }} />
                  <div style={{ width: `${Math.min(100 - lv.mastery_pct, 100.0 * lv.in_progress / (lv.total || 1))}%`, height: '100%', background: '#FBBF24' }} />
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 11, fontWeight: 700, alignItems: 'center' }}>
                  <span style={{ padding: '3px 8px', background: '#E8F3D9', color: '#374151', borderRadius: 999 }}>✅ {lv.learned}</span>
                  <span style={{ padding: '3px 8px', background: '#FEF3C7', color: '#374151', borderRadius: 999 }}>📖 {lv.in_progress}</span>
                  <span style={{ padding: '3px 8px', background: '#F3F4F6', color: '#374151', borderRadius: 999 }}>⬜ {lv.not_yet}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/vocab/words?level=${encodeURIComponent(lv.level.toLowerCase())}`); }}
                    style={{
                      marginLeft: 'auto', padding: '4px 10px', borderRadius: 999,
                      border: `1px solid ${meta.accent}55`, background: HT.white,
                      color: meta.accent, fontSize: 11, fontWeight: 800, cursor: 'pointer',
                    }}
                  >📖 Sổ từ →</button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  const STATS = [
    { emoji: '🔥', value: streak, label: 'STREAK', sub: 'ngày liên tiếp', bg: HT.yellow, img: `${V2}/icon-streak.png` },
    { emoji: '⭐', value: stars, label: 'STARS', sub: 'tích lũy', bg: HT.white, img: `${V2}/icon-stars.png`, bordered: true },
    { emoji: '🛡️', value: shields, label: 'SHIELDS', sub: 'đã cứu', bg: HT.greenShield, img: `${V2}/icon-shields.png` },
    { emoji: '🎯', value: `${readiness}%`, label: 'SẴN SÀNG', sub: 'Flyers exam', bg: HT.pinkBg, img: `${V2}/icon-readiness.png` },
  ];
  const WEEK_DAYS = ['T2','T3','T4','T5','T6','T7','CN'];
  const MISSION_STEPS = ['1. Warm-up', '2. Practice', '3. Boss quiz'];
  const HEADER_NAV = [
    { label: 'Trang Chủ', emoji: '🏠', url: '/dashboard', active: true },
    { label: 'Đề Thi Thử', emoji: '📄', url: '/full-mock-test' },
    { label: 'Thư Viện', emoji: '📚', url: '/vocab/words' },
    { label: 'Báo Cáo Tuần', emoji: '📊', url: '/report' },
    { label: 'Cài Đặt', emoji: '⚙️', url: '/settings' },
  ];
  const LEADERBOARD = [
    { r: 1, n: 'Vân Anh', s: 420, me: false, bg: 'linear-gradient(90deg, #FFE9A8, #FFF4CC)', borderColor: '#F4C95A', rankBg: HT.yellow, rankBorder: HT.yellowShadow, avatarBg: HT.teal },
    { r: 2, n: 'An Bình', s: 385, me: false, bg: '#F0F0F0', borderColor: '#D4D4D4', rankBg: '#D4D4D4', rankBorder: '#B4B4B4', avatarBg: HT.teal },
    { r: 3, n: userName, s: stars, me: true, bg: '#FFCDCB', borderColor: HT.coral, rankBg: '#F4A8A4', rankBorder: '#D88884', rankColor: 'white', avatarBg: HT.coral },
    { r: 4, n: 'Khôi Nguyên', s: 260, me: false, bg: HT.white, borderColor: HT.border, rankBg: HT.white, rankBorder: HT.border, avatarBg: HT.teal },
  ];

  const DesktopLayout = () => (
    <div className="hidden lg:block" style={{
      width: '100%', minHeight: '100vh',
      fontFamily: FONT_BODY, color: HT.text,
      background: HT.yellowBg, fontSize: 15, lineHeight: 1.5,
      position: 'relative', overflow: 'hidden',
    }}>
      <AppHeader />

      {/* ═══ BODY ═══ */}
      <main style={{
        maxWidth: 1440, margin: '0 auto',
        padding: '28px 40px 60px',
        display: 'grid', gridTemplateColumns: '1fr 340px', gap: 28,
      }}>
        <div>
        {/* Greeting + Search */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 14, color: HT.textMuted, marginBottom: 4 }}>{today}</div>
            <h1 style={{
              fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: 600,
              color: HT.text, margin: 0,
            }}>{greeting}, {userName}! <span style={{ fontSize: 22 }}>{greetingEmoji}</span></h1>
          </div>
          <div style={{ position: 'relative', width: 320, flexShrink: 0 }}>
            <input type="text" placeholder="Tìm topic, game, đề..." style={{
              width: '100%', padding: '12px 50px 12px 18px',
              border: `2px solid ${HT.yellow}`, borderRadius: 999,
              background: HT.white, fontFamily: 'inherit', fontSize: 14,
              color: HT.text, outline: 'none',
            }} />
            <button style={{
              position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
              width: 38, height: 38, background: HT.green,
              borderRadius: '50%', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: 16,
            }}>🔍</button>
          </div>
        </div>

        {/* ═══ HERO: checkered frame + banner bg ═══ */}
        <section style={{
          position: 'relative', borderRadius: 24, padding: 14,
          backgroundImage: `linear-gradient(${HT.cream}, ${HT.cream}), repeating-linear-gradient(45deg, ${HT.green} 0, ${HT.green} 12px, ${HT.yellow} 12px, ${HT.yellow} 24px)`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          border: '14px solid transparent',
          boxShadow: SHADOW_MD, overflow: 'hidden', marginBottom: 24,
        }}>
          <div style={{
            position: 'relative', borderRadius: 16, overflow: 'hidden',
            minHeight: 280, background: 'linear-gradient(135deg, #FFF8E7 0%, #FFEFC9 100%)',
          }}>
            <img src={`${V2}/banner.png`} alt="" style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'right center', zIndex: 0,
            }} />
            <div style={{ position: 'relative', zIndex: 2, padding: '28px 32px', maxWidth: '60%' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: HT.yellow, color: HT.brownDark,
                fontWeight: 700, fontSize: 13, letterSpacing: 0.5,
                padding: '8px 18px', borderRadius: 999,
                boxShadow: `0 3px 0 ${HT.yellowShadow}`, marginBottom: 20,
              }}>⭐ NHIỆM VỤ HÔM NAY</div>
              <h2 style={{
                fontFamily: FONT_DISPLAY, fontSize: 34, fontWeight: 600,
                color: HT.greenDark, margin: '0 0 10px', lineHeight: 1.15,
              }}>
                {weakItems[0]?.name ? `Chinh phục ${weakItems[0].name}` : 'Khám phá HuPath nào!'}
              </h2>
              <div style={{ color: HT.textSoft, fontSize: 14, marginBottom: 18, fontWeight: 600 }}>
                {weakItems[0]
                  ? `${weakItems[0].score}⭐ → mục tiêu ${weakItems[0].score + 20}⭐ · khoảng 5 phút · +1 streak day`
                  : 'Luyện vocab, thi thử, và nhận streak mỗi ngày!'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22, flexWrap: 'wrap' }}>
                {MISSION_STEPS.map((s, i) => (
                  <React.Fragment key={i}>
                    <span style={{
                      padding: '8px 16px', borderRadius: 999,
                      fontSize: 13, fontWeight: 700,
                      background: i === 0 ? HT.greenDark : HT.greenSoft,
                      color: i === 0 ? 'white' : HT.greenDark,
                    }}>{s}</span>
                    {i < MISSION_STEPS.length - 1 && (
                      <span style={{ width: 18, height: 2, background: HT.greenSoft, borderRadius: 2 }} />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <button onClick={() => navigate(weakItems[0]?.url || '/topics')} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  padding: '13px 26px', borderRadius: 999,
                  fontFamily: FONT_BODY, fontWeight: 800, fontSize: 14, letterSpacing: 0.5,
                  background: HT.coralDark, color: 'white', border: 'none',
                  boxShadow: `0 4px 0 ${HT.coralPress}`, cursor: 'pointer',
                }}>BẮT ĐẦU NGAY ▶</button>
                <button style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  padding: '13px 26px', borderRadius: 999,
                  fontFamily: FONT_BODY, fontWeight: 800, fontSize: 14, letterSpacing: 0.5,
                  background: 'white', color: HT.greenDark,
                  border: `2px solid ${HT.greenDark}`, cursor: 'pointer',
                }}>ĐỂ SAU</button>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ LỘ TRÌNH HÔM NAY (3-step journey) ═══ */}
        {journey?.has_journey && (
          <section style={{
            background: HT.white, borderRadius: 16, padding: 20,
            boxShadow: SHADOW_SM, border: `1px solid ${HT.border}`,
            marginBottom: 24,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{
                  fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 600,
                  color: HT.text, display: 'flex', alignItems: 'center', gap: 8,
                }}>🛤️ Lộ trình hôm nay</div>
                <div style={{ fontSize: 13, color: HT.textMuted, marginTop: 2 }}>
                  Tập trung topic <b>{journey.topic.name}</b> · {journey.topic.mastery_pct}% thuộc · 3 bước
                </div>
              </div>
            </div>
            <div style={{
              display: 'grid', gap: 12,
              gridTemplateColumns: 'repeat(3, 1fr)',
            }}>
              {journey.steps.map((s, i) => {
                const stepColors = ['#FEF3C7', '#FEEDD6', '#FFE7E1'];
                const stepBorder = ['#FBBF24', '#F97316', '#EF4444'];
                const stepDot = ['#92400E', '#9A3412', '#9F1239'];
                const stepNum = i + 1;
                const disabled = !s.available;
                const done = s.completed_today;
                return (
                  <div
                    key={i}
                    onClick={() => !disabled && navigate(s.cta.url)}
                    style={{
                      background: disabled ? '#F9FAFB' : stepColors[i],
                      border: `2px solid ${disabled ? '#E5E7EB' : stepBorder[i]}`,
                      borderRadius: 14,
                      padding: 14,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.6 : 1,
                      position: 'relative',
                      transition: 'all 0.15s',
                      minHeight: 140,
                      display: 'flex', flexDirection: 'column',
                    }}
                    onMouseEnter={(e) => {
                      if (!disabled) e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {/* Step number badge */}
                    <div style={{
                      position: 'absolute', top: 8, right: 10,
                      width: 24, height: 24, borderRadius: '50%',
                      background: stepDot[i], color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 800, fontFamily: FONT_DISPLAY,
                    }}>{stepNum}</div>
                    {done ? (
                      <div style={{
                        position: 'absolute', top: 8, left: 10,
                        background: '#10B981', color: '#fff',
                        padding: '2px 8px', borderRadius: 999,
                        fontSize: 10, fontWeight: 800,
                      }}>✓ XONG</div>
                    ) : null}

                    <div style={{ fontSize: 32, marginBottom: 6 }}>{s.icon}</div>
                    <div style={{
                      fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 15,
                      color: HT.text, lineHeight: 1.2, marginBottom: 6,
                    }}>{s.title}</div>
                    <div style={{
                      fontSize: 11, color: HT.textSoft, lineHeight: 1.3,
                      flex: 1, marginBottom: 10,
                    }}>{s.description}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 'auto' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: stepDot[i],
                        background: '#fff', padding: '3px 7px', borderRadius: 999,
                      }}>~{s.duration_min} phút</span>
                      <span style={{
                        marginLeft: 'auto', fontSize: 12, fontWeight: 800, color: stepDot[i],
                      }}>{s.cta.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ═══ STATS ═══ */}
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          {STATS.map((s, i) => (
            <div key={i} style={{
              position: 'relative', borderRadius: 16, padding: 18,
              overflow: 'hidden', minHeight: 120,
              background: s.bg, boxSizing: 'border-box',
              border: s.bordered ? `2px solid ${HT.border}` : '2px solid transparent',
              boxShadow: SHADOW_SM,
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            }}>
              <div style={{ fontSize: 22, position: 'relative', zIndex: 1 }}>{s.emoji}</div>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: 600,
                  lineHeight: 1, color: HT.text, marginBottom: 4,
                }}>{s.value}</div>
                <div style={{
                  fontSize: 13, fontWeight: 700, letterSpacing: 0.8,
                  color: HT.text, textTransform: 'uppercase',
                  whiteSpace: 'nowrap', overflow: 'visible',
                }}>{s.label}</div>
                <div style={{ fontSize: 12, color: HT.textSoft, marginTop: 2, whiteSpace: 'nowrap' }}>{s.sub}</div>
              </div>
              <img src={s.img} alt="" style={{
                position: 'absolute', right: -4, top: '50%',
                transform: 'translateY(-50%)',
                width: 90, height: 90, objectFit: 'contain', zIndex: 0, opacity: 0.95,
              }} />
            </div>
          ))}
        </section>

        {/* ═══ HỌC TỪ VỰNG THEO CẤP ĐỘ ═══ */}
        {renderLevelVocab()}

        {/* ═══ ĐỀ XUẤT HÔM NAY ═══ */}
        {recommendedTopics.length > 0 && (
          <section style={{
            background: HT.white, borderRadius: 16, padding: 20,
            boxShadow: SHADOW_SM, border: `1px solid ${HT.border}`,
            marginBottom: 24,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{
                  fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 600,
                  color: HT.text, display: 'flex', alignItems: 'center', gap: 8,
                }}>📌 Đề xuất hôm nay</div>
                <div style={{ fontSize: 13, color: HT.textMuted, marginTop: 2 }}>
                  Chủ điểm Hu chọn cho bé hôm nay
                </div>
              </div>
              <a href="#" onClick={(e) => { e.preventDefault(); navigate('/topics'); }} style={{
                color: HT.coralDark, fontWeight: 700, fontSize: 13, textDecoration: 'none',
              }}>Tất cả chủ điểm →</a>
            </div>
            <div style={{
              display: 'grid', gap: 12,
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            }}>
              {recommendedTopics.map((t) => {
                const isVocab = t.tag_type_code === 'vocabulary_topic';
                const accent = isVocab ? '#1e40af' : HT.greenDark;
                const bg = isVocab ? '#EFF6FF' : HT.greenBg;
                const emoji = isVocab ? '🌊' : '🌳';
                const badgeColor = t.review_due > 0 ? '#FEF2F2' : t.in_progress > 0 ? '#FEF3C7' : '#F3F4F6';
                const badgeText = t.review_due > 0 ? `🔴 ${t.review_due} cần ôn`
                  : t.in_progress > 0 ? `🟡 ${t.in_progress} đang học`
                  : `⚪ ${t.unexplored} chưa học`;
                return (
                  <div
                    key={t.tag_id}
                    onClick={() => navigate(t.cta?.url || `/topics/${t.tag_id}`)}
                    style={{
                      background: bg, border: `2px solid ${accent}22`, borderRadius: 14,
                      padding: 14, cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = SHADOW_MD;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ fontSize: 28 }}>{emoji}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 15, color: HT.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.name}
                        </div>
                        <div style={{ fontSize: 11, color: HT.textMuted, marginTop: 2 }}>
                          {t.mastered}/{t.total} · {t.mastery_pct}%
                        </div>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, height: 5, background: '#fff', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${t.mastery_pct}%`, height: '100%', background: accent }} />
                    </div>
                    <div style={{
                      marginTop: 8, padding: '3px 8px', display: 'inline-block',
                      background: badgeColor, color: '#374151',
                      borderRadius: 999, fontSize: 11, fontWeight: 700,
                    }}>{badgeText}</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ═══ PANELS: Quests + Cần cải thiện ═══ */}
        <section style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Quests */}
          <div style={{
            background: HT.white, borderRadius: 16, padding: 20,
            boxShadow: SHADOW_SM, border: `1px solid ${HT.border}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{
                  fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 600,
                  color: HT.text, display: 'flex', alignItems: 'center', gap: 8,
                }}>🎯 Quests hôm nay</div>
                <div style={{ fontSize: 13, color: HT.textMuted, marginTop: 2 }}>Hoàn thành cả 3 để nhận +15⭐ bonus!</div>
              </div>
              <span style={{
                background: HT.yellow, color: HT.brownDark,
                fontWeight: 700, padding: '6px 14px', borderRadius: 999,
                fontSize: 13, boxShadow: `0 2px 0 ${HT.yellowShadow}`,
              }}>{questDone}/{questTotal}</span>
            </div>
            <div style={{ height: 6, background: HT.greenBg, borderRadius: 999, overflow: 'hidden', margin: '8px 0 14px' }}>
              <div style={{ height: '100%', background: HT.green, borderRadius: 999, width: `${questTotal ? (questDone/questTotal)*100 : 0}%`, transition: 'width 0.4s ease' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {questsArr.slice(0, 3).map((q, i) => {
                const cta = {
                  complete_quizzes: '/quest/quiz-session',
                  review_weak_tag: weakItems[0]?.url || '/topics',
                  practice_listening: '/full-mock-test',
                  practice_reading: '/full-mock-test',
                  score_high: '/full-mock-test',
                  complete_full_test: '/full-mock-test',
                }[q.quest_type] || '/topics';
                return (
                  <div key={q.id || i} onClick={() => !q.is_completed && navigate(cta)} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '12px 14px',
                    border: `1px solid ${HT.border}`, borderRadius: 10,
                    background: HT.white, cursor: q.is_completed ? 'default' : 'pointer',
                    opacity: q.is_completed ? 0.6 : 1,
                  }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: 6,
                      border: `2px solid ${q.is_completed ? HT.green : HT.border}`,
                      background: q.is_completed ? HT.green : 'transparent',
                      flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 900, fontSize: 14,
                    }}>{q.is_completed ? '✓' : ''}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: HT.text, textDecoration: q.is_completed ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.description}</div>
                      <div style={{ fontSize: 12, color: HT.textMuted }}>
                        {q.is_completed ? '✓ Hoàn thành' : `${q.current_value || 0}/${q.target_value || 1}`}
                      </div>
                    </div>
                    <span style={{
                      background: '#FFF3D4', color: HT.brownDark,
                      fontWeight: 700, padding: '6px 12px', borderRadius: 999,
                      fontSize: 13, border: `1.5px solid ${HT.yellow}`, flexShrink: 0,
                    }}>+{q.stars_reward || 5}⭐</span>
                  </div>
                );
              })}
              {questsArr.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: HT.textMuted, fontSize: 13 }}>Đang tải quests...</div>
              )}
            </div>
          </div>

          {/* Cần cải thiện */}
          <div style={{
            background: HT.white, borderRadius: 16, padding: 20,
            boxShadow: SHADOW_SM, border: `1px solid ${HT.border}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{
                  fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 600,
                  color: HT.text, display: 'flex', alignItems: 'center', gap: 8,
                }}>✏️ Cần cải thiện</div>
                <div style={{ fontSize: 13, color: HT.textMuted, marginTop: 2 }}>Hu gợi ý {weakItems.length} điểm yếu cần ôn</div>
              </div>
              <a href="#" onClick={(e) => { e.preventDefault(); navigate('/report'); }} style={{
                color: HT.coralDark, fontWeight: 700, fontSize: 13, textDecoration: 'none',
              }}>Xem hết →</a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {weakItems.map((w, i) => {
                const tagColor = w.tag === 'SKILL' ? HT.pink : w.tag === 'GRAMMAR' ? '#A2D2FF' : w.tag === 'TOPIC' ? HT.greenDark : HT.yellowShadow;
                const rowBg = w.tag === 'SKILL' ? HT.pinkBg : w.tag === 'GRAMMAR' ? '#F0F8FF' : w.tag === 'TOPIC' ? '#CDEBE1' : '#FFF6E0';
                const progressColor = w.tag === 'SKILL' ? HT.coral : w.tag === 'GRAMMAR' ? '#5A9BD5' : w.tag === 'TOPIC' ? HT.green : HT.yellow;
                return (
                  <div key={i} onClick={() => navigate(w.url)} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 14px',
                    background: rowBg,
                    borderRadius: 10, cursor: 'pointer',
                  }}>
                    <span style={{
                      background: tagColor,
                      color: 'white', fontWeight: 800, fontSize: 10,
                      padding: '4px 10px', borderRadius: 6, letterSpacing: 0.8, flexShrink: 0,
                    }}>{w.tag}</span>
                    <span style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 14, color: HT.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.name}</span>
                    <div style={{ width: 80, height: 4, background: 'rgba(0,0,0,0.1)', borderRadius: 999, overflow: 'hidden', flexShrink: 0 }}>
                      <div style={{ width: `${w.score}%`, height: '100%', background: progressColor }} />
                    </div>
                    <span style={{ fontWeight: 700, fontSize: 14, color: HT.textSoft, minWidth: 20, textAlign: 'right', flexShrink: 0 }}>{w.score}</span>
                  </div>
                );
              })}
              {weakItems.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: HT.textMuted, fontSize: 13 }}>🎉 Không có điểm yếu!</div>
              )}
            </div>
          </div>
        </section>

        {/* ═══ ĐÃ THUỘC GẦN ĐÂY ═══ */}
        {recentlyMastered.length > 0 && (
          <section style={{
            background: HT.white, borderRadius: 16, padding: 20,
            boxShadow: SHADOW_SM, border: `1px solid ${HT.border}`,
            marginTop: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{
                  fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 600,
                  color: HT.text, display: 'flex', alignItems: 'center', gap: 8,
                }}>✨ Đã thuộc gần đây</div>
                <div style={{ fontSize: 13, color: HT.textMuted, marginTop: 2 }}>
                  Bé vừa master {recentlyMastered.length} từ trong 7 ngày qua
                </div>
              </div>
            </div>
            <div style={{
              display: 'grid', gap: 10,
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            }}>
              {recentlyMastered.map((m) => {
                const masteredAt = new Date(m.mastered_at);
                const daysAgo = Math.floor((Date.now() - masteredAt.getTime()) / 86400000);
                const ago = daysAgo === 0 ? 'Hôm nay' : daysAgo === 1 ? 'Hôm qua' : `${daysAgo} ngày trước`;
                return (
                  <div
                    key={m.leaf_tag_id}
                    onClick={() => navigate(m.cta?.url || `/topics/${m.parent_topic_id}`)}
                    style={{
                      background: '#F0FDF4', border: '2px solid #86EFAC',
                      borderRadius: 12, padding: 12, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 10,
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ fontSize: 22 }}>🏆</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 14, color: '#14532D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.leaf_name}
                      </div>
                      <div style={{ fontSize: 11, color: '#16A34A' }}>
                        {m.parent_topic_name} · {ago}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        </div>

        {/* ═══ RIGHT SIDEBAR ═══ */}
        <aside style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Leaderboard */}
          <div style={{
            background: HT.white, borderRadius: 16, padding: 20,
            boxShadow: SHADOW_SM, border: `1px solid ${HT.border}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{
                fontFamily: FONT_DISPLAY, fontSize: 17, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>🏆 Bảng xếp hạng</div>
              <span style={{
                background: HT.yellowSoft, color: HT.brownDark,
                fontWeight: 800, fontSize: 11, padding: '5px 12px',
                borderRadius: 999, letterSpacing: 1,
              }}>TUẦN</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {LEADERBOARD.map((p, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 10,
                  border: `${p.me ? 2 : 1}px solid ${p.borderColor}`,
                  background: p.bg,
                }}>
                  <span style={{
                    width: 26, height: 26, borderRadius: '50%',
                    background: p.rankBg, border: `1.5px solid ${p.rankBorder}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: 13, color: p.rankColor || HT.text,
                    flexShrink: 0,
                  }}>{p.r === 4 ? '#4' : p.r}</span>
                  <span style={{
                    width: 28, height: 28, borderRadius: 8,
                    background: p.avatarBg, color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: 13, flexShrink: 0,
                  }}>{(p.n[0] || 'U').toUpperCase()}</span>
                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.n}</span>
                    {p.me && <small style={{ fontWeight: 500, color: HT.textMuted, fontSize: 11, lineHeight: 1, marginTop: 2 }}>Bạn</small>}
                  </div>
                  <span style={{ fontWeight: 800, fontSize: 13, color: HT.text, flexShrink: 0 }}>{p.s}⭐</span>
                </div>
              ))}
            </div>
          </div>

          {/* Streak */}
          <div style={{
            background: HT.white, borderRadius: 16, padding: 20,
            boxShadow: SHADOW_SM, border: `1px solid ${HT.border}`,
          }}>
            <div style={{
              fontFamily: FONT_DISPLAY, fontSize: 17, fontWeight: 600,
              marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6,
            }}>🔥 Chuỗi {streak} ngày</div>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 6, marginTop: 26, marginBottom: 14,
            }}>
              {WEEK_DAYS.map((d, i) => {
                const done = streak > 0 && i < (streak % 7 || 7);
                return (
                  <div key={i} style={{
                    aspectRatio: '1',
                    border: done ? `2px solid ${HT.yellow}` : `2px dashed ${HT.border}`,
                    borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 700,
                    color: done ? HT.brownDark : HT.textMuted,
                    background: done ? HT.yellow : 'transparent',
                    position: 'relative',
                  }}>
                    <span style={{ position: 'absolute', top: -20, fontSize: 11, color: HT.textMuted, fontWeight: 700 }}>{d}</span>
                    {done ? '🔥' : ''}
                  </div>
                );
              })}
            </div>
            <div style={{
              textAlign: 'center', fontSize: 12, color: HT.textSoft,
              paddingTop: 8, borderTop: `1px dashed ${HT.border}`,
            }}>Học hôm nay để không mất streak! 🔥</div>
          </div>

          {/* Hu Says */}
          <div style={{
            background: 'linear-gradient(135deg, #5E9E3E, #4A8830)',
            color: 'white', borderRadius: 16, padding: 18,
            position: 'relative', boxShadow: SHADOW_MD,
            marginTop: 20,
          }}>
            <img src={`${V2}/owl-mini.png`} alt="Hu" style={{
              position: 'absolute', top: '50%', right: -10,
              transform: 'translateY(-50%)',
              width: 115, height: 115, pointerEvents: 'none',
            }} />
            <div style={{
              fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 14, letterSpacing: 1,
              marginBottom: 8, background: 'rgba(255,255,255,0.15)',
              display: 'inline-block', padding: '4px 12px', borderRadius: 999,
            }}>HU NÓI</div>
            <div style={{ fontSize: 14, lineHeight: 1.5, fontWeight: 500, paddingRight: 70 }}>
              "Nay mình ôn Listening rồi được thưởng 3 cái mới luôn nhaa!" 🎁
            </div>
          </div>
        </aside>
      </main>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  // MOBILE LAYOUT (<lg) — simplified single column
  // ═══════════════════════════════════════════════════════════════
  const MobileLayout = () => {
    const MOBILE_STATS = [
      { emoji: '🔥', v: streak, l: 'STREAK', bg: HT.yellow, img: `${V2}/icon-streak.png` },
      { emoji: '⭐', v: stars, l: 'STARS', bg: HT.white, img: `${V2}/icon-stars.png`, bordered: true },
      { emoji: '🛡️', v: shields, l: 'SHIELDS', bg: HT.greenShield, img: `${V2}/icon-shields.png` },
      { emoji: '🎯', v: `${readiness}%`, l: 'SẴN SÀNG', bg: HT.pinkBg, img: `${V2}/icon-readiness.png` },
    ];
    return (
    <div className="flex lg:hidden flex-col" style={{
      width: '100%', minHeight: '100vh',
      fontFamily: FONT_BODY, color: HT.text,
      background: HT.yellowBg,
    }}>
      {/* Mobile header — cream bg + yellow border + ribbons + logo pill */}
      <header style={{
        position: 'relative', background: HT.cream,
        borderBottom: `4px solid ${HT.yellow}`,
        boxShadow: SHADOW_SM,
        zIndex: 100,
      }}>
        <img src={`${V2}/ribbon-left.png`} alt="" aria-hidden="true" style={{
          position: 'absolute', top: 0, left: 0,
          width: 78, height: 'auto',
          pointerEvents: 'none', zIndex: 0,
          filter: 'drop-shadow(0 2px 3px rgba(139,111,71,0.15))',
        }} />
        <img src={`${V2}/ribbon-left.png`} alt="" aria-hidden="true" style={{
          position: 'absolute', top: 0, right: 0,
          width: 78, height: 'auto',
          transform: 'scaleX(-1)',
          pointerEvents: 'none', zIndex: 0,
          filter: 'drop-shadow(0 2px 3px rgba(139,111,71,0.15))',
        }} />
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '6px 14px',
          position: 'relative',
        }}>
          <div onClick={() => navigate('/dashboard')} style={{
            display: 'inline-flex', alignItems: 'center', gap: 2, lineHeight: 1,
            fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 18,
            color: HT.greenDark, cursor: 'pointer',
            padding: '2px 12px 2px 2px',
            background: HT.white, border: `2px solid ${HT.yellow}`, borderRadius: 999,
            boxShadow: `0 2px 0 ${HT.yellowShadow}`,
            position: 'relative', zIndex: 1,
          }}>
            <img src={`${V2}/owl-mini.png`} alt="HuPath" style={{ width: 36, height: 36, objectFit: 'contain', display: 'block', flexShrink: 0 }} />
            <span>HuPath</span>
          </div>
          <div style={{ flex: 1 }} />
          <div ref={mobileUserMenuRef} style={{ position: 'relative', zIndex: 100 }}>
            <div
              onClick={() => setShowMobileUserMenu((v) => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                cursor: 'pointer',
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: HT.coral, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14,
                border: `2px solid ${HT.yellow}`,
              }}>{(userName[0] || 'U').toUpperCase()}</div>
              <span style={{ color: HT.textMuted, fontSize: 12 }}>▼</span>
            </div>
            {showMobileUserMenu && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                background: HT.white, borderRadius: 12,
                border: `2px solid ${HT.yellow}`,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                minWidth: 160, overflow: 'hidden',
                fontFamily: FONT_BODY,
              }}>
                <button onClick={() => { setShowMobileUserMenu(false); navigate('/settings'); }} style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '12px 16px', border: 'none', background: 'transparent',
                  cursor: 'pointer', fontSize: 14, fontWeight: 600, color: HT.text,
                  fontFamily: FONT_BODY,
                }}>
                  <span style={{ fontSize: 16 }}>⚙️</span> Cài Đặt
                </button>
                <div style={{ height: 1, background: HT.yellow, opacity: 0.4 }} />
                <button onClick={() => { setShowMobileUserMenu(false); logout(navigate); }} style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '12px 16px', border: 'none', background: 'transparent',
                  cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#D64545',
                  fontFamily: FONT_BODY,
                }}>
                  <span style={{ fontSize: 16 }}>🚪</span> Đăng Xuất
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div style={{ padding: '16px 16px 32px' }}>
        {/* Greeting */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: HT.textMuted, fontWeight: 600 }}>{today}</div>
          <div style={{
            fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 600,
            color: HT.text, marginTop: 2, lineHeight: 1.2,
          }}>{greeting}, {userName}! <span style={{ fontSize: 18 }}>{greetingEmoji}</span></div>
        </div>

        {/* HERO — banner bg + mission copy */}
        <section style={{
          position: 'relative', borderRadius: 20, padding: 8,
          backgroundImage: `linear-gradient(${HT.cream}, ${HT.cream}), repeating-linear-gradient(45deg, ${HT.green} 0, ${HT.green} 10px, ${HT.yellow} 10px, ${HT.yellow} 20px)`,
          backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box',
          border: '8px solid transparent',
          boxShadow: SHADOW_MD, overflow: 'hidden', marginBottom: 16,
        }}>
          <div style={{
            position: 'relative', borderRadius: 12, overflow: 'hidden',
            minHeight: 180, background: 'linear-gradient(135deg, #FFF8E7 0%, #FFEFC9 100%)',
          }}>
            <img src={`${V2}/banner.png`} alt="" style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'right center', zIndex: 0,
            }} />
            <div style={{ position: 'relative', zIndex: 2, padding: '16px 18px' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: HT.yellow, color: HT.brownDark,
                fontWeight: 700, fontSize: 10, letterSpacing: 0.5,
                padding: '5px 12px', borderRadius: 999,
                boxShadow: `0 2px 0 ${HT.yellowShadow}`, marginBottom: 10,
              }}>⭐ NHIỆM VỤ HÔM NAY</div>
              <div style={{
                fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 600,
                color: HT.greenDark, lineHeight: 1.15, marginBottom: 6,
              }}>{weakItems[0]?.name ? `Chinh phục ${weakItems[0].name}` : 'Khám phá HuPath!'}</div>
              <div style={{ color: HT.textSoft, fontSize: 12, marginBottom: 12, fontWeight: 600 }}>
                {weakItems[0] ? `${weakItems[0].score}⭐ → ${weakItems[0].score + 20}⭐ · 5 phút` : 'Bắt đầu hành trình mới!'}
              </div>
              <button onClick={() => navigate(weakItems[0]?.url || '/topics')} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 20px', borderRadius: 999,
                fontFamily: FONT_BODY, fontWeight: 800, fontSize: 13, letterSpacing: 0.5,
                background: HT.coralDark, color: 'white', border: 'none',
                boxShadow: `0 3px 0 ${HT.coralPress}`, cursor: 'pointer',
              }}>BẮT ĐẦU NGAY ▶</button>
            </div>
          </div>
        </section>

        {/* 4 Stats 2x2 */}
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 20 }}>
          {MOBILE_STATS.map((s, i) => (
            <div key={i} style={{
              position: 'relative', borderRadius: 14, padding: '12px 14px',
              overflow: 'hidden', minHeight: 78,
              background: s.bg,
              border: s.bordered ? `1px solid ${HT.border}` : 'none',
              boxShadow: SHADOW_SM,
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            }}>
              <div style={{ fontSize: 16, position: 'relative', zIndex: 1 }}>{s.emoji}</div>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 26, fontWeight: 600, lineHeight: 1, color: HT.text }}>{s.v}</div>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.8, color: HT.text, textTransform: 'uppercase', marginTop: 2 }}>{s.l}</div>
              </div>
              <img src={s.img} alt="" style={{
                position: 'absolute', right: -4, top: '50%',
                transform: 'translateY(-50%)',
                width: 58, height: 58, objectFit: 'contain', zIndex: 0, opacity: 0.95,
              }} />
            </div>
          ))}
        </section>

        {/* Quests */}
        {questsArr.length > 0 && (
          <div style={{
            background: HT.white, border: `1px solid ${HT.border}`,
            borderRadius: 14, padding: 16, boxShadow: SHADOW_SM, marginBottom: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 10 }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 600, color: HT.text }}>🎯 Quests hôm nay</div>
              <span style={{
                background: HT.yellow, color: HT.brownDark,
                fontWeight: 800, padding: '4px 10px', borderRadius: 999, fontSize: 11,
                boxShadow: `0 2px 0 ${HT.yellowShadow}`,
              }}>{questDone}/{questTotal}</span>
            </div>
            <div style={{ height: 5, background: HT.greenBg, borderRadius: 999, overflow: 'hidden', margin: '6px 0 12px' }}>
              <div style={{ height: '100%', background: HT.green, width: `${questTotal ? (questDone/questTotal)*100 : 0}%`, transition: 'width 0.4s' }} />
            </div>
            {questsArr.slice(0, 3).map((q, i) => (
              <div key={q.id || i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', border: `1px solid ${HT.border}`, borderRadius: 10,
                background: HT.white, marginBottom: 6,
                opacity: q.is_completed ? 0.55 : 1,
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: 6,
                  border: `2px solid ${q.is_completed ? HT.green : HT.border}`,
                  background: q.is_completed ? HT.green : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: 12, fontWeight: 900, flexShrink: 0,
                }}>{q.is_completed ? '✓' : ''}</div>
                <div style={{ flex: 1, fontSize: 12, fontWeight: 700, color: HT.text, textDecoration: q.is_completed ? 'line-through' : 'none' }}>{q.description}</div>
                <span style={{
                  background: '#FFF3D4', color: HT.brownDark,
                  fontWeight: 700, padding: '3px 8px', borderRadius: 999,
                  fontSize: 11, border: `1px solid ${HT.yellow}`,
                }}>+{q.stars_reward}⭐</span>
              </div>
            ))}
          </div>
        )}

        {/* Level vocabulary (continuous) */}
        {renderLevelVocab()}

        {/* Weak items */}
        {weakItems.length > 0 && (
          <div style={{
            background: HT.white, border: `1px solid ${HT.border}`,
            borderRadius: 14, padding: 16, boxShadow: SHADOW_SM, marginBottom: 16,
          }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 600, color: HT.text, marginBottom: 10 }}>✏️ Cần cải thiện</div>
            {weakItems.slice(0, 3).map((w, i) => {
              const isTopic = w.tag === 'TOPIC';
              return (
                <div key={i} onClick={() => navigate(w.url)} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', background: isTopic ? '#CDEBE1' : HT.pinkBg,
                  borderRadius: 10, marginBottom: 6, cursor: 'pointer',
                }}>
                  <span style={{
                    background: isTopic ? HT.greenDark : HT.pink, color: 'white',
                    fontWeight: 800, fontSize: 9, padding: '3px 8px', borderRadius: 6,
                    letterSpacing: 0.5, flexShrink: 0,
                  }}>{w.tag}</span>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: HT.text }}>{w.name}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: HT.textSoft }}>{w.score}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Explore tiles 2x2 */}
        <div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 600, color: HT.text, marginBottom: 10 }}>🌟 Khám phá</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { l: 'Chủ điểm', emoji: '📌', bg: HT.yellow, color: HT.brownDark, url: '/topics' },
              { l: 'Đề Thi', emoji: '📝', bg: HT.greenShield, color: HT.text, url: '/full-mock-test' },
              { l: 'Thư viện', emoji: '📚', bg: HT.pinkBg, color: HT.text, url: '/vocab/words' },
              { l: 'Thành tích', emoji: '🏆', bg: HT.coralDark, color: 'white', url: '/report' },
            ].map((a, i) => (
              <button key={i} onClick={() => navigate(a.url)} style={{
                background: a.bg, borderRadius: 14, padding: 14,
                border: 'none', boxShadow: SHADOW_SM,
                textAlign: 'left', fontFamily: FONT_BODY, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <div style={{ fontSize: 22 }}>{a.emoji}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: a.color }}>{a.l}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
    );
  };

  return (
    <>
      <DesktopLayout />
      <MobileLayout />
    </>
  );
};

export default Dashboard;
